import { z } from 'zod';
import { MessageType } from './messages.js';
// Protocol version for compatibility checking
export const PROTOCOL_VERSION = 1;
// Client connect validation
export const clientConnectSchema = z.object({
    type: z.literal(MessageType.CLIENT_CONNECT),
    tick: z.number().int().nonnegative(),
    playerName: z.string().min(1).max(32),
    protocolVersion: z.literal(PROTOCOL_VERSION),
});
// Pawn command validation
export const pawnCommandSchema = z.object({
    type: z.literal(MessageType.CLIENT_PAWN_COMMAND),
    tick: z.number().int().nonnegative(),
    commandType: z.number().int().min(0).max(255),
    pawnIds: z.array(z.number().int().nonnegative()).min(1).max(100),
    targetX: z.number().finite(),
    targetY: z.number().finite(),
    targetEntity: z.number().int().nonnegative().optional(),
    queue: z.boolean().optional(),
});
// Input validation
export const inputSchema = z.object({
    type: z.literal(MessageType.CLIENT_INPUT),
    tick: z.number().int().nonnegative(),
    inputFlags: z.number().int().min(0).max(255),
    mouseX: z.number().finite(),
    mouseY: z.number().finite(),
    sequence: z.number().int().nonnegative(),
});
// Chunk request validation
export const chunkRequestSchema = z.object({
    type: z.literal(MessageType.CLIENT_CHUNK_REQUEST),
    tick: z.number().int().nonnegative(),
    chunkX: z.number().int(),
    chunkY: z.number().int(),
});
// Ping validation
export const pingSchema = z.object({
    type: z.literal(MessageType.CLIENT_PING),
    tick: z.number().int().nonnegative(),
    clientTime: z.number().nonnegative(),
});
// Camera update validation
export const cameraUpdateSchema = z.object({
    type: z.literal(MessageType.CLIENT_CAMERA_UPDATE),
    tick: z.number().int().nonnegative(),
    cameraX: z.number().finite(),
    cameraY: z.number().finite(),
    viewWidth: z.number().positive(),
    viewHeight: z.number().positive(),
});
// Construction order validation
export const constructionOrderSchema = z.object({
    type: z.literal(MessageType.CLIENT_CONSTRUCTION_ORDER),
    tick: z.number().int().nonnegative(),
    objectType: z.number().int().min(0).max(65535), // ObjectTypeValue
    tileX: z.number().int(),
    tileY: z.number().int(),
    pawnIds: z.array(z.number().int().nonnegative()).min(0).max(100),
});
// Cancel construction validation
export const cancelConstructionSchema = z.object({
    type: z.literal(MessageType.CLIENT_CANCEL_CONSTRUCTION),
    tick: z.number().int().nonnegative(),
    siteNetworkId: z.number().int().nonnegative(),
});
// Craft order validation
export const craftOrderSchema = z.object({
    type: z.literal(MessageType.CLIENT_CRAFT_ORDER),
    tick: z.number().int().nonnegative(),
    workstationNetworkId: z.number().int().nonnegative(),
    recipeId: z.string().min(1).max(64),
    quantity: z.number().int().min(0).max(100), // 0 = make all available
    pawnIds: z.array(z.number().int().nonnegative()),
});
// Cancel craft validation
export const cancelCraftSchema = z.object({
    type: z.literal(MessageType.CLIENT_CANCEL_CRAFT),
    tick: z.number().int().nonnegative(),
    orderId: z.number().int().nonnegative(),
});
// Open workstation validation
export const openWorkstationSchema = z.object({
    type: z.literal(MessageType.CLIENT_OPEN_WORKSTATION),
    tick: z.number().int().nonnegative(),
    workstationNetworkId: z.number().int().nonnegative(),
});
// Pawn selection validation
export const pawnSelectionSchema = z.object({
    type: z.literal(MessageType.CLIENT_PAWN_SELECTION),
    tick: z.number().int().nonnegative(),
    selectedPawnIds: z.array(z.number().int().nonnegative()).max(100),
});
// Respawn validation
export const respawnSchema = z.object({
    type: z.literal(MessageType.CLIENT_RESPAWN),
    tick: z.number().int().nonnegative(),
});
// Chat message validation
export const chatMessageSchema = z.object({
    content: z.string().min(1).max(500),
});
// Server configuration schema
export const serverConfigSchema = z.object({
    port: z.number().int().min(1024).max(65535).default(9001),
    maxPlayers: z.number().int().min(1).max(500).default(100),
    tickRate: z.number().int().min(1).max(60).default(20),
    worldSeed: z.number().int().optional(),
    dbPath: z.string().default('./data/world.db'),
});
// Message type to schema mapping for validation lookup
const MESSAGE_VALIDATORS = new Map([
    [MessageType.CLIENT_CONNECT, clientConnectSchema],
    [MessageType.CLIENT_INPUT, inputSchema],
    [MessageType.CLIENT_PAWN_COMMAND, pawnCommandSchema],
    [MessageType.CLIENT_CHUNK_REQUEST, chunkRequestSchema],
    [MessageType.CLIENT_PING, pingSchema],
    [MessageType.CLIENT_CAMERA_UPDATE, cameraUpdateSchema],
    [MessageType.CLIENT_CONSTRUCTION_ORDER, constructionOrderSchema],
    [MessageType.CLIENT_CANCEL_CONSTRUCTION, cancelConstructionSchema],
    [MessageType.CLIENT_CRAFT_ORDER, craftOrderSchema],
    [MessageType.CLIENT_CANCEL_CRAFT, cancelCraftSchema],
    [MessageType.CLIENT_OPEN_WORKSTATION, openWorkstationSchema],
    [MessageType.CLIENT_PAWN_SELECTION, pawnSelectionSchema],
    [MessageType.CLIENT_RESPAWN, respawnSchema],
]);
// Validate client messages based on type
export const validateClientMessage = ({ message, }) => {
    if (typeof message !== 'object' || message === null) {
        return { success: false, error: 'Invalid message format' };
    }
    const msg = message;
    const type = msg['type'];
    const schema = MESSAGE_VALIDATORS.get(type);
    if (!schema) {
        return { success: false, error: `Unknown message type: ${type}` };
    }
    const result = schema.safeParse(message);
    if (result.success) {
        return { success: true, data: result.data };
    }
    return { success: false, error: result.error.message };
};
//# sourceMappingURL=schemas.js.map